import React, {useEffect, useState} from 'react';

import {StorableItemType} from 'constants/ModelTypeConstants';
import {PotentialChildStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LOCATION_PICKER_SEARCH_FINISHED_EVENT,
  LOCATION_PICKER_SEARCH_STARTED_EVENT,
} from 'performance/processInventoryEvents';
import {StorablesInputSearchResponse} from 'samples/StorableDAO';
import {
  graphQLStorableSummaryToREST,
  restStorableSummaryToGraphQL,
} from 'samples/storables/graphql/translateStorable';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';

import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {ConnectedParentStorableSelectTree} from './ParentStorableSelectTree';

export interface ParentStorableSearchableSelectTreeProps {
  autoFocus?: boolean;
  onRemoveStorable: () => void;
  onSelectStorable: (storable: StorableSummaryGraphQL) => void;
  registryId?: string | null;
  selectedStorable?: StorableSummaryGraphQL | null;
  storableTypes: Array<StorableItemType>;
  borderless?: boolean;
  includeArchived?: boolean;
  allowNone?: boolean;
  /** List of child storables that will be moved into the selected location. If provided, will result in
   * rendering available space information & deprioritization. Schemas are included to support checking a
   * location's allowed schemas, but if missing simply the number of items will be considered. */
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
  // See comment in MoveStorablesModal
  numStorablesToBeMovedMayChange?: boolean;
}

/**
 * Component allows for both searching a parent storable and selecting one from a tree.
 *
 * Search results are added to the redux store (StorableStore) as soon as the search call completes. This
 * happens in `handleSearchSuccess()` in StorableSearchInput by way of the
 * `STORABLE_SUMMARIES_FETCH_COMPLETED` event. Once they're in the store, ParentStorableSelectTreeConnector
 * finds them and adds them to the tree.
 *
 * As a result, by the time you select a search result, that item (and its ancestors) are already in the tree.
 * The selected item is passed via the prop `selectedStorable` and then to SelectTree via prop `selectedIds`.
 */
export default function ParentStorableSearchableSelectTree({
  autoFocus,
  onRemoveStorable,
  onSelectStorable,
  registryId,
  selectedStorable,
  storableTypes,
  borderless,
  allowNone,
  storablesToBeMoved,
  numStorablesToBeMovedMayChange,
  includeArchived = false,
}: ParentStorableSearchableSelectTreeProps): JSX.Element {
  const selectedStorables = selectedStorable ? [selectedStorable] : [];
  const [storablesToBeMovedSchema, setStorablesToBeMovedSchema] = useState<
    {id: string; name: string} | undefined
  >(undefined);

  useEffect(() => {
    // If all items are of the same schema, preserve that schema for checking whether storables can be moved.
    // Otherwise, conservatively omit the schema since we don't know which will remain selected
    if (storablesToBeMoved != null && storablesToBeMoved.length > 0) {
      const firstSchema = storablesToBeMoved[0].storableSchema;
      if (
        firstSchema != null &&
        storablesToBeMoved.slice(1).every(({storableSchema}) => storableSchema?.id === firstSchema.id)
      ) {
        setStorablesToBeMovedSchema(firstSchema);
      }
    }
  }, [storablesToBeMoved]);

  return (
    <div {...getTestProps({component: 'ParentStorableSearchableSelectTree', element: 'root'})}>
      <StorableSearchInput
        autoFocus={autoFocus}
        disabled={!registryId}
        onChange={(storables) => {
          if (storables.length) {
            onSelectStorable(restStorableSummaryToGraphQL(storables[0]));
          } else {
            onRemoveStorable();
          }
        }}
        registryId={registryId}
        selectedStorables={selectedStorables.map((s) => graphQLStorableSummaryToREST(s))}
        storableTypes={storableTypes}
        onSearchStart={() => {
          logProfilerTimingEvent(LOCATION_PICKER_SEARCH_STARTED_EVENT, {component: 'StorableSearchInput'});
        }}
        onSearchSuccess={(resp: StorablesInputSearchResponse) => {
          logProfilerTimingEvent(LOCATION_PICKER_SEARCH_FINISHED_EVENT, {
            searchLimit: resp.limit,
            component: 'StorableSearchInput',
            numItemsReturned: resp.total,
          });
        }}
        storablesToBeMoved={storablesToBeMoved}
      />
      <ConnectedParentStorableSelectTree
        onSelectStorable={onSelectStorable}
        registryId={registryId!}
        selectedStorable={selectedStorable}
        storableTypes={storableTypes}
        borderless={borderless}
        includeArchived={includeArchived}
        allowNone={allowNone}
        // See comment in MoveStorablesModal
        storablesToBeMoved={
          numStorablesToBeMovedMayChange && storablesToBeMoved != null && storablesToBeMoved.length > 0
            ? [{storableSchema: storablesToBeMovedSchema}]
            : storablesToBeMoved
        }
      />
    </div>
  );
}
